import { FILE_MAX_BYTES, isImage, isText, kindOf, mediaTypeOf } from "@rally/agreed";
import type { IconName } from "@rally/ui-kit";

export const CHIPS = ["All", "Text", "Code", "Images", "Data", "Video"] as const;
export type Chip = (typeof CHIPS)[number];

export function glyphForChip(chip: Chip): IconName | null {
  if (chip === "Text") return "file-text";
  if (chip === "Code") return "code";
  if (chip === "Images") return "image";
  if (chip === "Data") return "database";
  if (chip === "Video") return "video";
  return null;
}

const CODE = new Set([
  "ts",
  "tsx",
  "js",
  "jsx",
  "mjs",
  "cjs",
  "sql",
  "sh",
  "bash",
  "py",
  "rb",
  "go",
  "rs",
  "css",
  "yml",
  "yaml",
  "toml",
  "ini",
  "env",
]);

export function endOf(name: string): string {
  const parts = name.toLowerCase().split(".");
  return parts.length > 1 ? (parts.pop() ?? "") : "";
}

export function chipOf(file: { name: string; mediaType: string }): Chip | null {
  const type = mediaTypeOf(file.mediaType, file.name);
  const kind = kindOf(type);
  if (kind === "Image") return "Images";
  if (kind === "Video") return "Video";
  if (kind === "Data") return "Data";
  if (isText(type)) return CODE.has(endOf(file.name)) ? "Code" : "Text";
  return null;
}

export function countedBy(files: ReadonlyArray<{ name: string; mediaType: string }>): Record<Chip, number> {
  const counts = { All: files.length, Text: 0, Code: 0, Images: 0, Data: 0, Video: 0 };
  for (const one of files) {
    const chip = chipOf(one);
    if (chip && chip !== "All") counts[chip] += 1;
  }
  return counts;
}

export function under<T extends { name: string; mediaType: string }>(files: readonly T[], chip: Chip): T[] {
  return chip === "All" ? [...files] : files.filter((one) => chipOf(one) === chip);
}

export function glyphFor(file: { name: string; mediaType: string }): IconName {
  const type = mediaTypeOf(file.mediaType, file.name);
  const kind = kindOf(type);
  if (kind === "Image") return "image";
  if (kind === "Video") return "video";
  if (kind === "Audio") return "music";
  if (kind === "Data") return "database";
  if (isText(type)) return CODE.has(endOf(file.name)) ? "code" : "file-text";
  return "file";
}

export function drawsInline(file: { name: string; mediaType: string }): boolean {
  return isImage(mediaTypeOf(file.mediaType, file.name));
}

export function typeSaid(file: { name: string; mediaType: string }): string {
  const end = endOf(file.name);
  if (end) return end.toUpperCase();
  const type = mediaTypeOf(file.mediaType, file.name);
  return (type.split("/")[1] ?? type).toUpperCase();
}

export function sizeSaid(bytes: number): string {
  if (bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 ? Math.round(size) : Math.round(size * 10) / 10} ${units[unit]}`;
}

export function tooBig(bytes: number): boolean {
  return bytes > FILE_MAX_BYTES;
}

export function tooBigSaid(bytes: number): string {
  return `${sizeSaid(bytes)} is over the ${sizeSaid(FILE_MAX_BYTES)} limit. Send a smaller one.`;
}
